import React, { useMemo, useRef } from 'react';
import Slider from 'react-slick';
import { useRouter } from 'next/router';
import SliderButton from '@components/common/sliderButton';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { usePositionJobGroups, usePositionProfileMatching } from '@repository/position/usePosition';
import useProfileConditions from '@repository/profiles/useProfileConditions';
import PersonalStatus from './pesonalStatus';
import PersonalPositionListItem from './personalPositionListItem';
import { PositionJobCardDto } from '@domain/rs/position/jd/PositionJobListCardRs';
import ArrayUtil from '@utils/array/util';
import { V2Route } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './personalPositionList.module.scss';
const cx = classnames.bind(styles);

const PrevArrow = (props: any) => {
  const { className, style, onClick } = props;

  return (
    <SliderButton
      type={'prev'}
      slickClass={className}
      handleClick={onClick}
      style={{ ...style }}
      customClassName={cx('btnPrev')}
    />
  );
};

const NextArrow = (props: any) => {
  const { className, style, onClick } = props;

  return (
    <SliderButton
      type={'next'}
      slickClass={className}
      handleClick={onClick}
      style={{ ...style }}
      customClassName={cx('btnNext')}
    />
  );
};

const PersonalPositionList = () => {
  const router = useRouter();
  const dragging = useRef(false);
  const settings = {
    infinite: false,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    slidesToShow: 1,
    speed: 300,
    rows: 1,
    slidesPerRow: 4,
    dots: true,
    cssEase: 'ease-in-out',
    draggable: true,
    beforeChange: () => (dragging.current = true),
    afterChange: () => (dragging.current = false),
  };

  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const { data: positionListData } = usePositionProfileMatching({ pageSize: 11, groupingDuplicate: true, hide: true });
  const invalidLength = (positionListData?.positions?.length ?? 0) < 11;
  const { data: profileConditionData } = useProfileConditions(isLogin && invalidLength);
  const firstJobGroupCode = profileConditionData?.jobGroups[0]?.jobGroupCode;
  const { data: positionJobGroupData } = usePositionJobGroups({
    jobGroupCode: firstJobGroupCode,
    hide: true,
    pageSize: 11,
  });

  const getPositionList = () => {
    let newPositionList = ArrayUtil.shuffleArray(positionListData?.positions || []);
    if (invalidLength && positionJobGroupData?.positions?.length) {
      const neededPositions = 11 - newPositionList.length;
      const addedPositions = positionJobGroupData.positions
        .filter((position) => !newPositionList.some((p) => p.positionSn === position.positionSn))
        .slice(0, neededPositions);

      const shuffleAddedPositions = ArrayUtil.shuffleArray(addedPositions);
      newPositionList = newPositionList.concat(shuffleAddedPositions as unknown as PositionJobCardDto);
    }

    return newPositionList;
  };

  const title = isLogin ? '나에게 딱 맞는 추천 포지션' : '로그인하고 내 조건에 맞는 포지션 추천받기';

  const handleClick = (positionSn: number) => {
    if (dragging.current) return;
    router.push(V2Route.getPositionJDPage(positionSn));
  };

  const personalPositionListItems = useMemo(() => {
    const positionList = getPositionList();
    return positionList.map((item) => (
      <div className={cx('positionInner')} key={item.positionSn}>
        <PersonalPositionListItem key={item.positionSn} data={item} dragging={() => dragging.current} />
      </div>
    ));
  }, [positionListData]);

  return (
    <div className={cx('section')}>
      <div className={cx('sectionInner')}>
        <div className={cx('recruitmentListArea')}>
          <div className={cx('sectionTitleArea')}>
            <strong className={cx('sectionTitle')}>{title}</strong>
          </div>

          {/* [D] 개인화 포지션 노출 */}
          <div className={cx('recruitmentListInner')}>
            <Slider className={cx('positionSlider')} {...settings}>
              <div className={cx('positionInner')}>
                <PersonalStatus />
              </div>
              {personalPositionListItems}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalPositionList;
